import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Loader2, MailCheck } from "lucide-react";
import { toast } from "sonner";
import Wordmark from "@/components/Wordmark";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setSent(true);
      toast.success(data.message);
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail);
      setError(msg);
      toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5 py-12 bg-background">
      <div className="w-full max-w-sm">
        <Link to="/" className="flex items-center gap-2 mb-8"><Wordmark iconClass="h-8 w-auto" textClass="text-lg" /></Link>
        {sent ? (
          <div data-testid="forgot-sent">
            <MailCheck className="h-10 w-10 text-accent" />
            <h1 className="font-display text-3xl font-semibold mt-4">Check your inbox</h1>
            <p className="text-muted-foreground mt-2 text-sm">
              If <span className="font-medium">{email}</span> is registered, we've sent a link to reset your password.
              The link expires in 1 hour.
            </p>
            <Link to="/login" className="inline-block mt-8 text-accent font-medium text-sm" data-testid="forgot-back-login">Back to log in</Link>
          </div>
        ) : (
          <>
            <h1 className="font-display text-3xl font-semibold">Forgot your password?</h1>
            <p className="text-muted-foreground mt-1 text-sm">Enter your email and we'll send you a reset link.</p>
            {error && <div className="mt-5 p-3 rounded-xl bg-destructive/10 text-destructive text-sm" role="alert" data-testid="forgot-error">{error}</div>}
            <form onSubmit={submit} className="space-y-4 mt-6">
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email"
                className="w-full px-4 py-3 rounded-xl border border-border bg-background outline-none focus:border-accent" data-testid="forgot-email" />
              <button type="submit" disabled={busy}
                className="w-full py-3 rounded-full bg-accent text-white font-medium hover:bg-accent/90 transition-colors flex items-center justify-center gap-2 disabled:opacity-50" data-testid="forgot-submit">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} Send reset link
              </button>
            </form>
            <p className="text-center text-sm text-muted-foreground mt-6">
              Remembered it? <Link to="/login" className="text-accent font-medium">Log in</Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
